import { useState, type ReactNode } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
/** Figma Dev Handoff `4398:4798` — arrow-left-line (shaft + head); MD outlined back matches. */
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import { NotificationBell } from '@/features/notifications'
import { useSessionStore, type Role } from '@/stores/useSessionStore'
import { useInstrument } from '@/features/instruments/api/instrument-queries'
import { questionBuilderBackPath } from '@/features/instruments/lib/instrumentNavigation'
import {
  INSTRUMENT_HEADER_BACK_TITLE_GAP_PX,
  INSTRUMENT_HEADER_TITLE_TYPOGRAPHY_SX,
  instrumentHeaderBackButtonSx,
  instrumentWorkspaceRailBoxSx,
} from '@/features/instruments/lib/instrumentWorkspaceLayout'
import {
  APP_SHELL_HEADER_HEIGHT_PX,
  APP_SHELL_HEADER_PADDING_BOTTOM_PX,
  APP_SHELL_HEADER_PADDING_TOP_PX,
  APP_SHELL_HEADER_ROW_HEIGHT_PX,
  APP_SHELL_NAV_PERSISTENT_BREAKPOINT,
  APP_SHELL_SIDEBAR_WIDTH_PX,
} from '@/shared/components/AppShell/appShellLayout'
import { AppShellSidebar, type AppShellNavItem } from '@/shared/components/AppShell/AppShellSidebar'
import { TruncatedTooltipText } from '@/shared/components/TruncatedTooltipText'
import {
  resolveAppShellHeaderTestId,
  resolveHeaderSearchChangeHandler,
  resolveHeaderSearchValue,
  resolveShellBackAriaLabel,
  resolveWorkspaceBackTestId,
} from '@/shared/components/AppShell/appShellHeaderHelpers'
import {
  runInstrumentCreateBack,
  runInstrumentWorkspaceBack,
} from '@/shared/components/AppShell/appShellNavigationHandlers'
import {
  campaignWizardBackPath,
} from '@/features/campaigns/lib/campaignWizardNavigation'
import { deriveAppShellRouteFlags } from '@/shared/components/AppShell/appShellRouteFlags'

/** Figma `5037:6410` — Package Design Survey title in app header (B.Primary 2). */
const INSTRUMENT_HEADER_TITLE_COLOR = '#04183f'
import { useNotificationSocket } from '@/shared/hooks/useNotificationSocket'
import { useLogout } from '@/features/auth/api'
import {
  NavIconBookOpen,
  NavIconBuilding,
  NavIconDashboard,
  NavIconFileChart,
  NavIconGroup,
  NavIconLayout3,
  NavIconMegaphone,
  NavIconNotification2,
  NavIconUser3,
} from '@/shared/components/AppShell/NavLineIcons'
import {
  formatSensemakerRoleLabel,
  resolveActiveSection,
  resolveNavClick,
} from '@/shared/components/AppShell/nav-section'
import { appShellMainPaddingSx } from '@/shared/components/AppShell/appShellMainPadding'
import { AppShellHeaderSearch } from '@/shared/components/AppShell/AppShellHeaderSearch'
import { useCampaigns } from '@/features/campaigns/api/campaign-queries'
import { useCampaignLandingSearch } from '@/features/campaigns/lib/campaignLandingSearch'
import { resolveAppShellHeaderSearch } from '@/shared/components/AppShell/resolveAppShellHeaderSearch'
import { parseLoiDashboardPath } from '@/features/campaigns/lib/loiDashboardNavigation'
import { parseInstrumentDashboardPath, parseQuestionDrillDownPath } from '@/features/campaigns/lib/instrumentDashboardNavigation'
import { AppShellLoiDashboardStatus } from '@/shared/components/AppShell/AppShellLoiDashboardStatus'
import {
  AppShellAdminDashboardStatus,
  useShowAdminDashboardStatus,
} from '@/shared/components/AppShell/AppShellAdminDashboardStatus'
import { AppShellInstrumentDashboardStatus } from '@/shared/components/AppShell/AppShellInstrumentDashboardStatus'
import { AppShellQuestionDrillDownStatus } from '@/shared/components/AppShell/AppShellQuestionDrillDownStatus'
import { useLibraryTemplateSearch } from '@/features/library/lib/libraryTemplateSearch'
import { useSensemakerListSearch } from '@/features/tenants/lib/sensemaker-list-search'
import { useSignalProviderListSearch } from '@/features/signal-providers/lib/signal-provider-list-search'
import { useScrollAppShellMainToTop } from '@/shared/hooks/useScrollAppShellMainToTop'

interface NavItem extends AppShellNavItem {
  roles?: Role[]
}

const TENANT_NAV_ROLES: Role[] = ['ADMIN', 'EDITOR', 'VIEWER']
const PLATFORM_NAV_ROLES: Role[] = ['PLATFORM_ADMIN']

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/', section: 'dashboard', icon: <NavIconDashboard /> },
  {
    label: 'Research',
    path: '/campaigns',
    section: 'campaigns',
    icon: <NavIconMegaphone />,
    roles: TENANT_NAV_ROLES,
  },
  /** BFP-1008: Companies lives with Campaigns journey (Admin/Editor). Nested IA deferred — sibling after Research. */
  {
    label: 'Companies',
    path: '/companies',
    section: 'companies',
    icon: <NavIconBuilding />,
    roles: ['ADMIN', 'EDITOR'],
  },
  {
    label: 'Reports',
    path: '/reports',
    section: 'reports',
    icon: <NavIconFileChart />,
    roles: TENANT_NAV_ROLES,
  },
  {
    label: 'Library',
    path: '/library',
    section: 'library',
    icon: <NavIconBookOpen />,
    roles: ['ADMIN', 'EDITOR', 'VIEWER'],
  },
  {
    label: 'User Management',
    path: '/users',
    section: 'users',
    icon: <NavIconUser3 />,
    roles: ['ADMIN'],
  },
  {
    label: 'Sensemaker List',
    path: '/tenants',
    section: 'tenants',
    icon: <NavIconBuilding />,
    roles: PLATFORM_NAV_ROLES,
  },
  {
    label: 'Signal Providers',
    path: '/signal-providers',
    section: 'signalProviders',
    icon: <NavIconGroup />,
    roles: PLATFORM_NAV_ROLES,
  },
  {
    label: 'Notification Management',
    path: '/notification-management',
    section: 'notificationManagement',
    icon: <NavIconNotification2 />,
    roles: PLATFORM_NAV_ROLES,
  },
  {
    label: 'Template Library',
    path: '/platform/template-library',
    section: 'platformTemplateLibrary',
    icon: <NavIconLayout3 />,
    roles: PLATFORM_NAV_ROLES,
  },
]

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export function AppShell({ children }: Readonly<{ children: ReactNode }>) {
  const theme = useTheme()
  const isNavPersistent = useMediaQuery(theme.breakpoints.up(APP_SHELL_NAV_PERSISTENT_BREAKPOINT))
  const { user, sidebarOpen, toggleSidebar } = useSessionStore()
  const navigate = useNavigate()
  const location = useLocation()
  const mainRef = useScrollAppShellMainToTop()
  const [searchParams] = useSearchParams()
  const {
    isInstrumentCreateTemplates,
    isInstrumentCreateInfo,
    isInstrumentEditInfo,
    showInstrumentCreateBack,
    isQuestionBuilder,
    isInstrumentRouting,
    isPublish,
    campaignWizardParsed,
    isCampaignWizard,
    showInstrumentWorkspaceHeader,
    showCampaignWizardBack,
    showAppShellInstrumentTitle,
    isInstrumentWorkspaceGutterPage,
    usesInstrumentPageGutter,
    instrumentPageGutterPx,
    workspaceInstrumentId,
  } = deriveAppShellRouteFlags(location.pathname)
  const workspaceInstrumentQuery = useInstrument(workspaceInstrumentId, {
    enabled:
      !!workspaceInstrumentId &&
      (isQuestionBuilder || isInstrumentRouting || isPublish),
  })
  const instrumentHeaderTitle =
    workspaceInstrumentQuery.data?.name?.trim() || 'Package Design Survey'

  const handleInstrumentCreateBack = () => {
    runInstrumentCreateBack(navigate, {
      isInstrumentCreateTemplates,
      isInstrumentEditInfo,
      isInstrumentCreateInfo,
    })
  }

  const handleQuestionBuilderBack = () => {
    navigate(
      questionBuilderBackPath(workspaceInstrumentQuery.data?.campaignId),
    )
  }

  const handleCampaignWizardBack = () => {
    const id = campaignWizardParsed?.campaignId ?? ''
    navigate(campaignWizardBackPath(location.pathname, id))
  }

  const handleInstrumentWorkspaceBack = () => {
    runInstrumentWorkspaceBack({
      isPublish,
      isInstrumentRouting,
      workspaceInstrumentId,
      campaignId: workspaceInstrumentQuery.data?.campaignId?.trim() ?? '',
      returnToPreview: searchParams.get('returnToPreview') === '1',
      pathname: location.pathname,
      navigate,
      onQuestionBuilderBack: handleQuestionBuilderBack,
    })
  }
  const logout = useLogout()
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null)
  const profileMenuOpen = Boolean(profileMenuAnchor)

  useNotificationSocket()

  const visibleItems = navItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role)),
  )

  const displayName = user?.name ?? 'Liam Anderson'
  const initials = getInitials(displayName)
  const roleLabel = formatSensemakerRoleLabel(user?.role)

  const activeSection = resolveActiveSection(location.pathname, location.search)
  const { data: campaignsData } = useCampaigns()
  const hasCampaigns = (campaignsData?.items?.length ?? 0) > 0
  const { search: librarySearch, setSearch: setLibrarySearch } = useLibraryTemplateSearch()
  const { search: campaignSearch, setSearch: setCampaignSearch } = useCampaignLandingSearch()
  const { search: sensemakerSearch, setSearch: setSensemakerSearch } = useSensemakerListSearch()
  const { search: signalProviderSearch, setSearch: setSignalProviderSearch } =
    useSignalProviderListSearch()

  const closeMobileNav = () => {
    if (!isNavPersistent && sidebarOpen) {
      useSessionStore.setState({ sidebarOpen: false })
    }
  }

  const handleNavActivate = (item: NavItem) => {
    const result = resolveNavClick(item.section, item.path, location.pathname, location.search)
    if (result.action === 'navigate') {
      navigate(result.to)
      closeMobileNav()
    }
  }

  const handleProfileMenuOpen = (event: React.SyntheticEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null)
  }

  const handleAccountSettings = () => {
    handleProfileMenuClose()
    navigate('/settings')
  }

  const handleLogout = () => {
    handleProfileMenuClose()
    logout.mutate()
  }

  const shellBackAriaLabel = resolveShellBackAriaLabel({
    isInstrumentCreateInfo,
    isInstrumentRouting,
    isPublish,
    isCampaignWizard,
    campaignWizardStep: campaignWizardParsed?.step,
    isNewCampaignWizard: campaignWizardParsed?.campaignId === 'new',
    pathname: location.pathname,
  })
  const workspaceBackTestId = resolveWorkspaceBackTestId({
    isInstrumentRouting,
    isPublish,
    showInstrumentWorkspaceHeader,
    isCampaignWizard,
  })
  const showAppShellBack =
    showInstrumentCreateBack || showInstrumentWorkspaceHeader || showCampaignWizardBack
  const appShellHeaderTestId = resolveAppShellHeaderTestId(isInstrumentWorkspaceGutterPage)
  const headerSearchConfig = resolveAppShellHeaderSearch({
    activeSection,
    pathname: location.pathname,
    showAppShellBack,
    showAppShellInstrumentTitle,
    hasCampaigns,
  })
  const loiDashboardParams = parseLoiDashboardPath(location.pathname)
  const instrumentDashboardParams = parseInstrumentDashboardPath(location.pathname)
  const questionDrillDownParams = parseQuestionDrillDownPath(location.pathname)
  const showAdminDashboardStatus = useShowAdminDashboardStatus(
    location.pathname,
    user?.role,
  )
  const headerSearchValue = resolveHeaderSearchValue(
    headerSearchConfig.section,
    campaignSearch,
    librarySearch,
    sensemakerSearch,
    signalProviderSearch,
  )
  const handleHeaderSearchChange = resolveHeaderSearchChangeHandler(
    headerSearchConfig.section,
    setCampaignSearch,
    setLibrarySearch,
    setSensemakerSearch,
    setSignalProviderSearch,
  )

  const handleAppShellBack = () => {
    if (showCampaignWizardBack) {
      handleCampaignWizardBack()
      return
    }
    if (showInstrumentWorkspaceHeader) {
      handleInstrumentWorkspaceBack()
      return
    }
    handleInstrumentCreateBack()
  }

  const handleLogoClick = () => {
    navigate('/')
    closeMobileNav()
  }

  const sidebarProps = {
    visibleItems,
    activeSection,
    onNavActivate: handleNavActivate,
    displayName,
    initials,
    roleLabel,
    profileMenuAnchor,
    profileMenuOpen,
    onProfileMenuOpen: handleProfileMenuOpen,
    onProfileMenuClose: handleProfileMenuClose,
    onAccountSettings: handleAccountSettings,
    onLogout: handleLogout,
    logoutPending: logout.isPending,
    onLogoClick: handleLogoClick,
  }

  const sidebarShellSx = {
    width: APP_SHELL_SIDEBAR_WIDTH_PX,
    bgcolor: '#04183f',
    borderRight: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flexShrink: 0,
  } as const

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f9fafb' }}>
      {isNavPersistent ? (
        <Box
          component="aside"
          data-testid="app-shell-sidebar-persistent"
          sx={{
            ...sidebarShellSx,
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: 1200,
          }}
        >
          <AppShellSidebar {...sidebarProps} />
        </Box>
      ) : null}

      {isNavPersistent ? null : (
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={closeMobileNav}
          ModalProps={{ keepMounted: true }}
          data-testid="app-shell-sidebar-drawer"
          sx={{
            '& .MuiDrawer-paper': {
              width: APP_SHELL_SIDEBAR_WIDTH_PX,
              boxSizing: 'border-box',
              bgcolor: '#04183f',
            },
          }}
        >
          <Box sx={sidebarShellSx}>
            <AppShellSidebar {...sidebarProps} />
          </Box>
        </Drawer>
      )}

      <Box
        data-testid="app-shell-main-column"
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          ml: {
            xs: 0,
            [APP_SHELL_NAV_PERSISTENT_BREAKPOINT]: `${APP_SHELL_SIDEBAR_WIDTH_PX}px`,
          },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Box
          component="header"
          data-testid="app-shell-top-header"
          sx={{
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            height: `${APP_SHELL_HEADER_HEIGHT_PX}px`,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            pt: `${APP_SHELL_HEADER_PADDING_TOP_PX}px`,
            pb: `${APP_SHELL_HEADER_PADDING_BOTTOM_PX}px`,
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            flexShrink: 0,
          }}
        >
          <Box
            data-testid={appShellHeaderTestId}
            sx={{
              ...(isInstrumentWorkspaceGutterPage
                ? instrumentWorkspaceRailBoxSx()
                : {
                    boxSizing: 'border-box',
                    width: '100%',
                    pl: '32px',
                    pr: '32px',
                  }),
              display: 'flex',
              alignItems: 'center',
              minHeight: `${APP_SHELL_HEADER_ROW_HEIGHT_PX}px`,
              gap: showAppShellInstrumentTitle
                ? `${INSTRUMENT_HEADER_BACK_TITLE_GAP_PX}px`
                : '4px',
            }}
          >
            {isNavPersistent ? null : (
              <IconButton
                type="button"
                data-testid="app-shell-nav-menu"
                aria-label="Open navigation menu"
                onClick={toggleSidebar}
                sx={{
                  width: 36,
                  height: 44,
                  borderRadius: '10px',
                  color: '#0f172a',
                  flexShrink: 0,
                  mr: '4px',
                  '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
                }}
              >
                <MenuIcon sx={{ fontSize: 24 }} aria-hidden />
              </IconButton>
            )}
            {showAppShellBack ? (
              <IconButton
                type="button"
                data-testid={workspaceBackTestId}
                aria-label={shellBackAriaLabel}
                onClick={handleAppShellBack}
                sx={instrumentHeaderBackButtonSx()}
              >
                <ArrowBackOutlined sx={{ fontSize: 24 }} aria-hidden />
              </IconButton>
            ) : null}
            {showAppShellInstrumentTitle ? (
              <Box sx={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                <TruncatedTooltipText
                  text={instrumentHeaderTitle}
                  component="h1"
                  data-testid="app-shell-instrument-title"
                  typographySx={{
                    ...INSTRUMENT_HEADER_TITLE_TYPOGRAPHY_SX,
                    color: INSTRUMENT_HEADER_TITLE_COLOR,
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }} />
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: 0,
                gap: '12px',
                minWidth: 0,
                ml: headerSearchConfig.show ? { xs: '8px', sm: '16px' } : 0,
              }}
            >
              {headerSearchConfig.show ? (
                <AppShellHeaderSearch
                  value={headerSearchValue}
                  onChange={handleHeaderSearchChange}
                  placeholder={headerSearchConfig.placeholder}
                  testId={headerSearchConfig.testId}
                  ariaLabel={headerSearchConfig.ariaLabel}
                />
              ) : null}
              {showAdminDashboardStatus ? <AppShellAdminDashboardStatus /> : null}
              {loiDashboardParams ? (
                <AppShellLoiDashboardStatus loiId={loiDashboardParams.loiId} />
              ) : null}
              {instrumentDashboardParams ? (
                <AppShellInstrumentDashboardStatus
                  instrumentId={instrumentDashboardParams.instrumentId}
                />
              ) : null}
              {questionDrillDownParams ? (
                <AppShellQuestionDrillDownStatus
                  instrumentId={questionDrillDownParams.instrumentId}
                  questionId={questionDrillDownParams.questionId}
                />
              ) : null}
              <Box
                data-testid="sensemaker-ftux-notifications"
                sx={{
                  '& .MuiIconButton-root': {
                    width: 36,
                    height: 44,
                    borderRadius: '10px',
                    color: '#4a5565',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)', color: '#111827' },
                  },
                }}
              >
                <NotificationBell />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Page content */}
        <Box
          component="main"
          ref={mainRef}
          data-testid="app-shell-main"
          sx={{
            flex: 1,
            minHeight: 0,
            ...appShellMainPaddingSx(
              usesInstrumentPageGutter,
              isCampaignWizard,
              instrumentPageGutterPx,
            ),
            overflow: 'auto',
            overflowAnchor: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}
